import { useNavigate } from 'react-router-dom';
import { atlasOwnModel, type OwnModelResponse } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';

const FEW = 'Too few questions to say';
const COLS = 'display:grid;grid-template-columns:minmax(0,1.5fr) repeat(6,minmax(0,1fr));gap:10px;';
const NAME: Record<string, [string, string]> = {
  local: ['Our own model (Gemma)', 'Own server, inside our BAA'],
  mistral: ['Mistral (hosted)', 'Never sees resident information'],
};
const ms = (n: number) => (n >= 1000 ? (n / 1000).toFixed(1) + 's' : Math.round(n) + 'ms');

/**
 * Own-model progress, vs Claude (Spec V4 16 H, design T14076): owner only, at the top of
 * Atlas, Eval. Every figure comes from the shadow answers sealed on the Truth Ledger
 * (GET /atlas/own-model, which refuses everyone but the owner); a percent is hidden under 100
 * compared questions, and nothing is projected. The panel is absent for anyone the server
 * refuses, so it never shows to the Operator, the Developer or a facility role. The owner is
 * a Super Admin, so no other role asks at all (the route would only answer 403).
 */
export function OwnModelPanel() {
  const { client } = useSession();
  const { role } = useConsoleRole();
  const navigate = useNavigate();
  const q = useQuery<OwnModelResponse>((signal) => atlasOwnModel(client, { signal }), [client], { enabled: role === 'SUPER_ADMIN' });
  const d = q.data;
  if (!d) return null;
  const sh = d.shadowProvider;
  const min = d.minForPct ?? 100;
  const pctOf = (x: { n: number; pct: number | null }, compared: number) => (compared < min || x.pct === null ? FEW : Math.round(x.pct * 10) / 10 + '% · ' + x.n);
  return (
    <div data-screen-label="Own-model progress" data-testid="own-model-progress" style={sx('flex:0 0 auto;box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 2px 12px;margin-bottom:13px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;')} />
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Own-model progress · vs Claude</span>
        <span style={sx('display:inline-flex;flex:0 0 auto;white-space:nowrap;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:800;letter-spacing:.06em;color:#264C7E;background:rgba(47,92,151,.12);')}>OWNER ONLY</span>
        <span style={sx('font-size:16px;color:#54606C;')}>
          <span>{sh === 'off' ? 'Shadow is off, so nothing is being compared yet.' : 'Shadow is on (' + (sh === 'local' ? 'our own model' : 'Mistral') + '). ' + (d.candidates.some((c) => c.compared > 0) ? 'Comparing every answer.' : 'Waiting for the first answers to compare.')}</span>
        </span>
        <span style={sx('flex:1 1 40px;')} />
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/settings?section=atlas-assistant');
          }}
          style={sx('font-size:16px;font-weight:600;color:#264C7E;text-decoration:none;white-space:nowrap;')}
        >
          Shadow model in Settings →
        </a>
      </div>
      <div style={sx('overflow-x:auto;margin-top:8px;')}>
        <div style={sx('min-width:760px;')}>
          <div style={sx(COLS + 'padding:6px 2px;font-size:14px;letter-spacing:.06em;text-transform:uppercase;color:#54606C;font-weight:600;')}>
            <span>Candidate</span>
            <span>Questions compared</span>
            <span>Same tools as Claude</span>
            <span>Task success vs Claude</span>
            <span>Typical / slowest</span>
            <span>Error rate</span>
            <span>Jobs graduated</span>
          </div>
          {d.candidates.map((c) => {
            const [name, where] = NAME[c.key] ?? [c.key, ''];
            return (
              <div key={c.key} style={sx(COLS + 'align-items:baseline;padding:9px 2px;border-top:1px solid rgba(34,39,46,.1);font-size:16px;color:#22272E;font-variant-numeric:tabular-nums;')}>
                <span style={sx('min-width:0;')}>
                  <span style={sx('font-weight:700;')}>
                    <span>{name}</span>
                  </span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;')}>
                    <span>{where + (sh === c.key ? ' · shadow on' : '')}</span>
                  </span>
                </span>
                <span style={sx('font-weight:700;')}>
                  <span>{String(c.compared)}</span>
                </span>
                <span style={sx('color:#54606C;font-size:14px;')}>
                  <span>{pctOf(c.toolsSame, c.compared)}</span>
                </span>
                <span style={sx('color:#54606C;font-size:14px;')}>
                  <span>{c.taskSuccess === null ? FEW : String(c.taskSuccess)}</span>
                </span>
                <span style={sx('color:#54606C;font-size:14px;')}>
                  <span>{c.latency ? ms(c.latency.medianMs) + ' / ' + ms(c.latency.maxMs) : 'No answers yet'}</span>
                </span>
                <span style={sx('color:#54606C;font-size:14px;')}>
                  <span>{pctOf(c.errors, c.compared)}</span>
                </span>
                <span>
                  <span style={sx('font-weight:700;')}>
                    <span>{c.jobsGraduated === null ? '0 of 6' : c.jobsGraduated + ' of 6'}</span>
                  </span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;')}>
                    <span>{c.jobsGraduated === null ? 'exam not run' : 'exam judge'}</span>
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div style={sx('display:flex;gap:8px 22px;flex-wrap:wrap;padding-top:8px;border-top:1px solid rgba(34,39,46,.1);font-size:14px;color:#54606C;line-height:1.5;')}>
        <span style={sx('flex:1 1 320px;text-wrap:pretty;')}>
          <span style={sx('font-weight:700;color:#3C454F;')}>Weekly trend:</span>
          {' starts after the first full week of shadow answers. Percentages stay hidden until ' + min + ' questions are compared.'}
        </span>
        <span style={sx('flex:1 1 320px;text-wrap:pretty;')}>Counted from every shadow answer, never a hand-picked set. Task success comes from the exam judge (AF-1562, AF-881), not built yet.</span>
      </div>
    </div>
  );
}
